/**
 * One app-managed audible owner at a time.
 *
 * The app runs two players of its own: the YouTube console (PlayerQueueContext)
 * and the global Spotify embed (SpotifyPlayerContext). Nothing linked them, so
 * opening one left the other playing. Spotify's embed is cross-origin and its
 * audio cannot be observed, so this acts on the two things the app CAN see,
 * the explicit act of choosing each player:
 *
 * - The Spotify player opening (or switching playlist) pauses YouTube.
 * - An explicit YouTube request (a pick, Play, Resume: `playRequest`) closes
 *   the Spotify player. Automatic advances and observed state never do.
 *
 * Both act on CHANGES only. A Spotify player and a YouTube queue restored
 * together at boot are left as they are: the queue is silent until a tap, and
 * closing a restored player nobody touched would be the app deciding for them.
 * External Spotify tabs are out of scope; nothing here can see them.
 */
import { useEffect, useRef } from "react";
import { usePlayerQueue } from "@/contexts/PlayerQueueContext";
import { useSpotifyPlayerContext } from "@/contexts/SpotifyPlayerContext";

export function PlaybackOwnership() {
  const { playRequest, setPlaying } = usePlayerQueue();
  const { currentPlaylist, close: closeSpotify } = useSpotifyPlayerContext();
  const spotifyKey = currentPlaylist
    ? `${currentPlaylist.id}|${currentPlaylist.spotify_playlist_id}`
    : null;

  const lastSpotifyRef = useRef(spotifyKey);
  useEffect(() => {
    if (lastSpotifyRef.current === spotifyKey) return;
    lastSpotifyRef.current = spotifyKey;
    if (spotifyKey) setPlaying(false);
  }, [spotifyKey, setPlaying]);

  const spotifyOpenRef = useRef(!!spotifyKey);
  spotifyOpenRef.current = !!spotifyKey;
  const lastRequestRef = useRef(playRequest);
  useEffect(() => {
    if (lastRequestRef.current === playRequest) return;
    lastRequestRef.current = playRequest;
    if (spotifyOpenRef.current) closeSpotify();
  }, [playRequest, closeSpotify]);

  return null;
}
